<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-loading.fullscreen.lock="mainLoader"
    class="signup-ngs-page page"
    element-loading-background="rgba(255, 255, 255, 0.8)"
  >
    <div :class="`signup-ngs-page__header step-${currentStep}`">
      <div class="header__content__image"></div>
      <div class="header__content__bg"></div>
      <div class="header__content container">
        <el-row>
          <el-col
            :md="24"
            :lg="12"
          >
            <div
              id="main-content"
              class="header__content__wrapper"
            >
              <h2
                class="header__title"
                v-html="heroText"
              ></h2>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <div class="signup-ngs-page__body">
      <div class="container">
        <el-row>
          <el-col>
            <!-- Step 1 Personal and Company details -->
            <div
              v-show="currentStep === 1"
              class="body__content"
            >
              <validation-errors
                v-if="validationErrors.length && currentStep === 1"
                :errors="validationErrors"
              ></validation-errors>

              <el-form
                id="step1Form"
                ref="step1Form"
                :model="step1Form.form"
                :rules="step1FormRules"
                autocomplete="false"
                @submit.native.prevent
                @validate="handleFormFieldInput('step1Form')"
              >
                <h1 class="se-heading-m step-title">
                  <span class="color-primary">Step {{ currentStep }} / 3</span>
                  Personal and contact details
                </h1>

                <p class="se-body-l-screen mb-4">
                  In this section, tell us about you and the companies that you'd like our services to support.
                </p>

                <div class="step-hr"></div>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <label for="first_name">
                      First name
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="!hasInlineValidationErrorFor('step1Form','first_name')"
                        v-text="getInlineValidationErrorFor('step1Form','first_name')"
                      ></span>
                    </label>
                    <span class="is-custom-asterisk">*</span><br />
                    <el-form-item prop="first_name">
                      <el-input
                        id="first_name"
                        v-model="step1Form.form.first_name"
                        maxlength="64"
                        autocomplete="false"
                        label="Please enter your first name in this field"
                        aria-required="true"
                        @blur="step1Form.form.first_name = step1Form.form.first_name.trim()"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <label for="last_name">
                      Last name
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="!hasInlineValidationErrorFor('step1Form','last_name')"
                        v-text="getInlineValidationErrorFor('step1Form','last_name')"
                      ></span>
                    </label>
                    <span class="is-custom-asterisk">*</span><br />
                    <el-form-item prop="last_name">
                      <el-input
                        id="last_name"
                        v-model="step1Form.form.last_name"
                        maxlength="64"
                        autocomplete="false"
                        label="Please enter your last name in this field"
                        aria-required="true"
                        @blur="step1Form.form.last_name = step1Form.form.last_name.trim()"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <label for="email">
                      Email address<span class="is-custom-asterisk">*</span><br />
                      <p class="el-form-item__info">This will not be displayed on your profile</p>
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="!hasInlineValidationErrorFor('step1Form','email')"
                        v-text="getInlineValidationErrorFor('step1Form','email')"
                      ></span>
                    </label>
                    <el-form-item
                      prop="email"
                      class="is-no-asterisk"
                    >
                      <el-input
                        id="email"
                        v-model="step1Form.form.email"
                        type="email"
                        autocomplete="false"
                        label="Please enter your desired email address in this field"
                        aria-required="true"
                        @blur="step1Form.form.email = step1Form.form.email.trim()"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <label for="email_confirmation">Confirm email address
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="!hasInlineValidationErrorFor('step1Form','email_confirmation')"
                        v-text="getInlineValidationErrorFor('step1Form','email_confirmation')"
                      ></span>
                    </label>
                    <span class="is-custom-asterisk">*</span><br />
                    <el-form-item prop="email_confirmation">
                      <el-input
                        id="email_confirmation"
                        v-model="step1Form.form.email_confirmation"
                        type="email"
                        autocomplete="false"
                        label="Please confirm the desired email address in this field"
                        aria-required="true"
                        @blur="step1Form.form.email_confirmation = step1Form.form.email_confirmation.trim()"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <label for="organisation_name">
                      Organisation you work for
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="!hasInlineValidationErrorFor('step1Form','organisation_name')"
                        v-text="getInlineValidationErrorFor('step1Form','organisation_name')"
                      ></span>
                    </label>
                    <span class="is-custom-asterisk">*</span><br />
                    <el-form-item prop="organisation_name">
                      <el-input
                        id="organisation_name"
                        v-model="step1Form.form.organisation_name"
                        maxlength="64"
                        autocomplete="false"
                        label="Please enter your organisation name in this field"
                        aria-required="true"
                        :disabled="organisation != null"
                        @blur="step1Form.form.organisation_name = step1Form.form.organisation_name.trim()"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <label for="role">
                      Your role at the organisation
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="!hasInlineValidationErrorFor('step1Form','role')"
                        v-text="getInlineValidationErrorFor('step1Form','role')"
                      ></span>
                    </label>
                    <span class="is-custom-asterisk">*</span><br />
                    <el-form-item prop="role">
                      <el-input
                        id="role"
                        v-model="step1Form.form.role"
                        maxlength="64"
                        autocomplete="false"
                        label="Please enter your last name in this field"
                        aria-required="true"
                        @blur="step1Form.form.role = step1Form.form.role.trim()"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-form>

              <div class="submit-button-wrapper mt-4">
                <el-button
                  type="primary"
                  native-type="submit"
                  @click="submitStep1Form"
                >
                  Continue
                </el-button>
              </div>
            </div>
            <!-- Step 1 Personal and Company details End -->
            <!-- Step 2 Plans and experience -->
            <div
              v-show="currentStep === 2"
              class="body__content"
            >
              <el-button
                type="text"
                size="medium"
                class="step-goback-button align-self-start color-black"
                @click="gotoStep(1)"
              >
                <span aria-hidden="true">&lt; Back</span>
                <span class="sr-only">Go back a step</span>
              </el-button>

              <validation-errors
                v-if="validationErrors.length && currentStep === 2"
                :errors="validationErrors"
              ></validation-errors>

              <el-form
                id="step2Form"
                ref="step2Form"
                :model="step2Form.form"
                :rules="step2FormRules"
                autocomplete="false"
                @submit.native.prevent
                @validate="handleFormFieldInput('step2Form')"
              >
                <h1 class="se-heading-m step-title">
                  <span class="color-primary">Step {{ currentStep }} / 3</span>
                  Profile and settings
                </h1>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <p class="se-body-l-screen mb-4">
                      In this section you can add what type of support you can offer other services and also what you’d like to get from Community Wealth Builder.
                    </p>
                  </el-col>
                </el-row>

                <div class="step-hr mt-4"></div>

                <el-row>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                  >
                    <label for="experience">
                      About you and why you’re joining the network<span class="is-custom-asterisk">*</span><br />
                      <p class="el-form-item__info">Give other members some info about who you are e.g. what’s your background, skills, expertise and experience</p>
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="! hasInlineValidationErrorFor('step2Form','experience')"
                        v-text="getInlineValidationErrorFor('step2Form','experience')"
                      ></span>
                    </label>
                    <el-form-item
                      prop="experience"
                      class="is-no-asterisk"
                    >
                      <el-input
                        id="experience"
                        v-model="step2Form.form.experience"
                        type="textarea"
                        :rows="5"
                        maxlength="500"
                        label="Please give other members some info about who you are e.g. what’s your background, skills, expertise and experience in this field"
                        aria-required="true"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col
                    :md="24"
                    :lg="16"
                    :xl="14"
                    class="mt-n4 mb-4"
                  >
                    <span class="se-body-l-screen color-light-gray">You have {{ 500 - step2Form.form.experience.length }} characters remaining</span>
                  </el-col>
                </el-row>

                <el-row>
                  <el-col>
                    <label for="expertises">
                      <span aria-hidden="true">Expertise</span><span class="is-custom-asterisk">*</span>
                      <p class="el-form-item__info">(You can select multiple options)</p>
                      <span class="sr-only">Please select one or more Expertise</span>
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="! hasInlineValidationErrorFor('step2Form','expertises')"
                        v-text="getInlineValidationErrorFor('step2Form','expertises')"
                      ></span>
                    </label>
                    <el-form-item
                      id="expertises"
                      prop="expertises"
                    >
                      <el-checkbox-group
                        v-model="step2Form.form.expertises"
                      >
                        <el-row>
                          <el-col
                            v-for="expertise in step2Form.expertises"
                            :key="expertise.id"
                            :xs="12"
                            :md="12"
                            :lg="8"
                            class="mb-3"
                          >
                            <el-checkbox
                              name="edit_expertise_id"
                              :label="expertise.id"
                            >
                              {{ expertise.name }}
                            </el-checkbox>
                          </el-col>
                        </el-row>
                      </el-checkbox-group>
                    </el-form-item>
                  </el-col>
                </el-row> 
                <div class="step-hr"></div>  
                <el-row>
                  <el-col>
                    <label for="terms_agreed">
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="! hasInlineValidationErrorFor('step2Form','privacy_agreed')"
                        v-text="getInlineValidationErrorFor('step2Form','privacy_agreed')"
                      ></span>
                    </label>
                    <el-form-item
                      id="privacy_agreed"
                      prop="privacy_agreed"
                      class="mb-0"
                    >
                      <el-checkbox-group v-model="step2Form.form.privacy_agreed">
                        <el-checkbox
                          label="privacy_agreed_confirmed"
                          :value="0"
                          class="mb-3"
                        >
                          I acknowledge that I have read and understand the <router-link
                            :to="{ name: 'Cookie Policy' }"
                            class="text__link"
                            target="_blank"
                          >
                            privacy policy
                          </router-link>
                        </el-checkbox>
                      </el-checkbox-group>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row>
                  <el-col
                    class="mb-5"
                  >
                    <label for="terms_agreed">
                      <span
                        class="sr-only"
                        aria-live="polite"
                        :hidden="! hasInlineValidationErrorFor('step2Form','terms_agreed')"
                        v-text="getInlineValidationErrorFor('step2Form','terms_agreed')"
                      ></span>
                    </label>
                    <el-form-item
                      id="terms_agreed"
                      prop="terms_agreed"
                    >
                      <el-checkbox-group v-model="step2Form.form.terms_agreed">
                        <el-checkbox
                          label="terms_agreed_confirmed"
                          :value="1"
                        >
                          I acknowledge that I have read and understand the <router-link
                            :to="{ name: 'Terms & Conditions' }"
                            class="text__link"
                            target="_blank"
                          >
                            terms and conditions
                          </router-link>
                          for use of this site
                        </el-checkbox>
                      </el-checkbox-group>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-form>

              <div class="submit-button-wrapper mt-4">
                <el-button
                  type="primary"
                  native-type="submit"
                  @click="submitStep2Form"
                >
                  Complete registration
                </el-button>
              </div>
            </div>
            <!-- Step 2 Plans and experience End -->
            <!-- Step 4 Verify your account -->
            <div
              v-show="currentStep === 4"
              class="body__content"
            >
              <h1 class="se-heading-xl-40 mb-4">
                Thanks for submitting your application
              </h1>

              <el-row>
                <el-col
                  :md="24"
                  :lg="18"
                >
                  <p class="se-body-l-screen-19 mb-4">
                    We’ve sent you an email confirmation. We’ll review the information you’ve provided and let you know within 3 days whether or not your application has been approved. In the meantime, we might need to get in touch if we need more information.  
                  </p>
                  <p class="se-body-l-screen-19 mb-4">
                    If you need to speak to us about your application, <a
                      :href="contactUsUrl"
                      class="text__link"
                    >
                      just get in touch
                    </a>. <br />
                  </p>
                  <el-button
                    type="primary"
                    native-type="submit"
                    @click="gotoLink('/login')"
                  >
                    Back to Login
                  </el-button>
                </el-col>
              </el-row>
            </div>
            <!-- Step 4 Verify your account End -->
          </el-col>
        </el-row>
      </div>
    </div>
  </div>
</template>

<script>
import gsApi from '@/services/gs.api'
import validationErrors from '@/components/partials/validation-errors'
import {validate} from 'email-validator'

export default {
  name: 'SignupNgs',
  metaInfo: {
    title: "Join as a business member",
    meta: [{
      name: 'description',
      content: "Are you a business, entrepreneur or professional based in Scotland and looking for support? Join the free CWB network as a business member."
    }]
  },
  components: {

    validationErrors,
  },
  data :function() {

    const validateEmail = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('Enter a valid email address'))
      } else if (validate(value) == false) {
        callback(new Error('Enter a valid email address'))
      } else {
        callback()
      }
    }
    const validateEmailConfirmation = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('Email address doesn’t match. Make sure the email address in both email fields match.'))
      } else if (value !== this.step1Form.form.email) {
        callback(new Error("Email address doesn’t match. Make sure the email address in both email fields match."))
      } else {
        callback()
      }
    }

    return {
      validationErrors: [],
      mainLoader: false,
      currentStep: 1,
      step1Form: { // Step 1
        form: {
          first_name: '',
          last_name: '',
          email:'',
          email_confirmation:'',
          organisation_name: '',
          role: ''
        },
        inlineValidationErrors: [],
      },
      step1FormRules: {
        first_name: [
          { required: true, message: 'Enter your first name', trigger: 'blur' },
          { max: 64, message: 'First name may not be greater than 64 characters', trigger: 'blur' },
        ],
        last_name: [
          { required: true, message: 'Enter your last name', trigger: 'blur' },
          { max: 64, message: 'Last name may not be greater than 64 characters', trigger: 'blur' },
        ],
        email: [
          { validator: validateEmail, required: true },
        ],
        email_confirmation: [
          { validator: validateEmailConfirmation, required: true },
        ],
        organisation_name: [
          { required: true, message: 'Enter your organisation name', trigger: 'blur' },
          { max: 64, message: 'organisation may not be greater than 64 characters', trigger: 'blur' }
        ],
        role: [
          { required: true, message: 'Enter your role', trigger: 'blur' },
          { max: 64, message: 'Role may not be greater than 64 characters', trigger: 'blur' },
        ],
      },
      step2Form: { // Step 2
        form: {
          experience: '',
          expertises: [],
          privacy_agreed: [],
          terms_agreed:[]
        },
        expertises: [],
        inlineValidationErrors: [],
      },
      step2FormRules: {
        about_organisation: [
          { required: true, message: 'Tell us more about your company', trigger: 'blur' },
          { max: 500, message: 'experience may not be greater than 500 characters', trigger: 'change' },
        ],
        experience: [
          { required: true, message: 'Tell us more about you', trigger: 'blur' },
          { max: 500, message: 'experience may not be greater than 500 characters', trigger: 'change' },
        ],
        expertises: [
          { type: 'array', required: true, message: 'Select an expertises of business from the dropdown', trigger: 'change' }
        ],
        privacy_agreed: [
          { type: 'array', required: true, message: 'Check the box to confirm you agree to our privacy policy.', trigger: 'change' },
        ],
        terms_agreed: [
          { type: 'array', required: true, message: 'Check the box to confirm you agree to our terms and conditions.', trigger: 'change' },
        ],
      },
      inviteCode:null,
      inviteEmail: null,
      organisation: null
    }
  },
  computed: {
    heroText: function() {
      if(this.currentStep == 1) return '"Community Wealth Builders are people who want to give something back and I\'m delighted to be a part of that." <p class="mt-1">Community Wealth Builder Allan Rooney</p>'
      else if(this.currentStep == 2)  return '"Community Wealth Builder has had such a huge impact on our business - frankly, it could have been the difference between us making it or not." <p class="mt-1">David Capaldi, Opinurate</p>'
      return "Application complete! We’ll let you know soon."
    },
    avatarUrl: function () {
      return this.step1Form.avatarFile.url
    },
    contactUsUrl(){
      return `${process.env.VUE_APP_BE_WELL_CMS_URL}/for-my-organisation/contact-us-cwb`
    }
  },
  created() 
  {
    if (this.$route.query.hasOwnProperty("code")) {
      this.inviteCode = this.$route.query.code;
      this.inviteEmail = this.$route.query.email;
      this.validateInviteCode()
    }else{
      this.getRegisterMeta()
    }
  },
  methods: {
    validateInviteCode() {
      this.mainLoader = true
      gsApi.users.checkInviteCode({code: this.inviteCode})
        .then(response => {
          
          if (response.data.valid) {
            this.getRegisterMeta()
            this.organisation = response.data.organisation;
            this.step1Form.form.organisation_name = this.organisation ? this.organisation.name : null
            this.step1Form.form.email = this.$route.query.email || ''
            this
          } else {
            this.mainLoader = false
            this.$alert('The invite code is not valid. Please contact the CWB team for further assistance.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
          }
        }).catch(error => {
          this.mainLoader = false
          this.$alert('An unknown error occurred. Please try again later.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
          this.gotoLink('/home')
        })
    },
    submitStep1Form() {
      this.$refs['step1Form'].validate((valid) => {
        if (valid) {

          this.mainLoader = true

          gsApi.globalscot.validateStepOne(this.step1Form.form)
            .then(() => {
              this.validationErrors = [];
              this.gotoStep(2)
            })
            .catch(error => {
              this.validationErrors = [];
              if (error.response && error.response.status == 422 && error.response.data) {
                let errors = error.response.data.errors;
                
                for (let [key, value] of Object.entries(errors)) {
                  
                  let obj = {}
                  obj.id = key;
                  obj.message = value[0];
                  this.validationErrors.push(obj);
                }
                window.scrollTo({top: 0, behavior: 'smooth'});
              } else {
                this.$alert('There are errors in the form, please fix these errors and try again.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
              }
            }).finally(()=>{
              this.mainLoader = false
            })
        } else {
          this.validationErrors = [];
          this.$refs.step1Form.fields.forEach(field => {
            if(field.validateState == 'error') {
              let obj = {}
              obj.id = field.labelFor;
              obj.message = field.validateMessage;
              this.validationErrors.push(obj);
            }
          });
          this.$nextTick(() => {
            document.getElementById('formErrorBlock').scrollIntoView({ behavior: 'smooth', block: 'start' })
            let text = document.getElementById("step1Form").getElementsByClassName('el-form-item__error')[0].textContent
            this.$announcer.set(text)
          })
        }
      });
    },
    submitStep2Form() {
      this.$refs['step2Form'].validate((valid) => {
        if (valid) {
          const formData = new FormData()

          // Step 1
          formData.append('first_name', this.$options.filters.domPurify(this.step1Form.form.first_name, {ALLOWED_TAGS: []}))
          formData.append('last_name', this.$options.filters.domPurify(this.step1Form.form.last_name, {ALLOWED_TAGS: []}))
          formData.append('email', this.step1Form.form.email)
          formData.append('email_confirmation', this.step1Form.form.email_confirmation)
          
          formData.append('is_organisation',0);
          formData.append('organisation_name', this.step1Form.form.organisation_name)
          formData.append('role', this.step1Form.form.role)

          if(this.organisation)
          {
            formData.append('organisation_id', this.organisation.id)
            formData.append('code', this.inviteCode)
            formData.append('invite_email', this.inviteEmail)
          }
         
          // Step 2
          formData.append('experience', this.step2Form.form.experience)
          
          formData.append('expertises', [])
          this.step2Form.form.expertises.forEach((expertise) => {
            formData.append('expertises[]', expertise)
          })

          this.mainLoader = true

          gsApi.globalscot.register(formData)
            .then(() => {
              this.validationErrors = []
              this.mainLoader = false
              this.gotoStep(4)
            })
            .catch(error => {
              this.validationErrors = [];
              this.mainLoader = false

              if (error.response && error.response.status == 422 && error.response.data) {
                let errors = error.response.data.errors;
                for (let [key, value] of Object.entries(errors)) {
                  let obj = {}
                  obj.id = key;
                  obj.message = value[0];
                  this.validationErrors.push(obj);
                }
                window.scrollTo({top: 0, behavior: 'smooth'});
              } else {
                this.$alert('There are errors in the form, please fix these errors and try again.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
              }
            })
        } else {
          this.validationErrors = [];
          this.$refs.step2Form.fields.forEach(field => {
            if(field.validateState == 'error') {
              let obj = {}
              obj.id = field.labelFor;
              obj.message = field.validateMessage;
              this.validationErrors.push(obj);
            }
          });
          this.$nextTick(() => {
            document.getElementById('formErrorBlock').scrollIntoView({ behavior: 'smooth', block: 'start' })
            let text = document.getElementById("step2Form").getElementsByClassName('el-form-item__error')[0].textContent
            this.$announcer.set(text)
          })
        }
      });
    },
    getRegisterMeta() 
    {
      this.mainLoader = true

      gsApi.globalscot.meta.index()
        .then(response => {
          this.mainLoader = false
          this.step2Form.expertises = response.data.expertises.data

        }).catch(() => {
          this.mainLoader = false
          this.$alert('An unknown error occurred. Please try again later.', 'Error', { confirmButtonText: 'Close', showClose: false, customClass: 'notification-modal' });
          this.gotoLink('/home')
        })
    },
    gotoStep(step) {
      this.currentStep = step
      if (this.currentStep == 1) {
        this.$announcer.set('Step 1 of 3 - Personal and company details')
      } else if (this.currentStep == 2) {
        this.$announcer.set('Step 2 of 3 - Plans and experience')
      }
      window.scrollTo(0,0)
    },
    gotoLink(link) {
      this.$router.push(link)
    },
    handleFormFieldInput(formName) {
      this[formName].inlineValidationErrors = [];
      this.$refs[formName].fields.forEach(field => {
        if (field.validateState === 'error') {
          let obj = {};
          obj.id = field.labelFor;
          obj.message = field.validateMessage;
          this[formName].inlineValidationErrors.push(obj);
        }
      });
    },
    hasInlineValidationErrorFor(formName, fieldName) {
      return this.getInlineValidationErrorFor(formName, fieldName).length > 0;
    },
    getInlineValidationErrorFor(formName, fieldName) {
      const validationError = this[formName].inlineValidationErrors.find(error => error.id === fieldName);
      if (! validationError) {
        return '';
      }

      return validationError.message;
    },
  },
}
</script>

<style>
  .step-1-avatar img {
    width: 140px;
    height: 140px;
  }
</style>
